// What you can do to the clips you have selected, as buttons.
//
// The hotkeys are the fast path and they are not a path at all on a phone, where
// there is no keyboard and — until a long press grew one — there was no way to
// select two clips either. So every operation has a target here too, at a
// thumb's size, and each one names its own shortcut so a pointer learns the keys
// from the buttons rather than from a sheet nobody opens.
//
// Presentational: it is handed what it may do and what to call when asked.

import { useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ClipboardPaste,
  Copy,
  CopyPlus,
  Combine,
  Ellipsis,
  Repeat,
  Scissors,
  SquareSplitHorizontal,
  Trash2,
  VolumeX,
} from "lucide-react";
import { cn } from "@web-shared/lib/utils";

export interface ClipActionBarProps {
  can: { copy: boolean; paste: boolean; split: boolean; act: boolean };
  /** Whether the selection is muted — the mute button is a toggle, not a verb. */
  muted: boolean;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
  /** Live's Split, at the insert marker. */
  onSplit: () => void;
  /** Loop the transport over the selection (Live's Loop Selection). */
  onLoop: () => void;
  /** Live's Consolidate: each lane's part of the selection as one clip. */
  onConsolidate?: () => void;
  /** A consolidate is rendering — the button waits for it. */
  consolidating?: boolean;
  onNudge: (steps: number) => void;
  onMoveLane: (direction: -1 | 1) => void;
  onToggleMute: () => void;
  onDelete: () => void;
  /** A phone: the five verbs a thumb reaches for, labelled, in one row that
   * always fits; the rest behind More, as a second row rather than a menu. */
  compact?: boolean;
  /** With `compact`, a phone on its side: the row sits beside the clip's name
   * rather than under it, icon beside label — 390px of height has no room
   * for a second 56px row over the lanes. */
  dense?: boolean;
}

/** ⌘ where the keys say ⌘, Ctrl where they say Ctrl. A label that names the
 *  wrong key is worse than no label. */
const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? "⌘"
    : "Ctrl+";

export function ClipActionBar({
  can,
  muted,
  onCopy,
  onCut,
  onPaste,
  onDuplicate,
  onSplit,
  onLoop,
  onConsolidate,
  consolidating = false,
  onNudge,
  onMoveLane,
  onToggleMute,
  onDelete,
  compact = false,
  dense = false,
}: ClipActionBarProps) {
  const [more, setMore] = useState(false);
  if (compact) {
    // Twelve icons at 44px ran past a 390px screen with Delete off the end,
    // and none of them said what it was. Equal columns fit any width.
    return (
      <div
        className={cn(
          "min-w-0",
          dense ? "flex-1 pb-1.5 pt-3 [&_button]:h-9 [&_button]:flex-row [&_button]:gap-1.5" : "w-full basis-full",
        )}
        role="toolbar"
        aria-label="Clip actions"
      >
        <div className="grid grid-cols-6 gap-1">
          <Labelled label="Copy" disabled={!can.copy} onClick={onCopy} icon={<Copy className="size-4" />} />
          <Labelled label="Paste" disabled={!can.paste} onClick={onPaste} icon={<ClipboardPaste className="size-4" />} />
          <Labelled label="Duplicate" short="Dupe" disabled={!can.copy} onClick={onDuplicate} icon={<CopyPlus className="size-4" />} />
          <Labelled label="Split" disabled={!can.split} onClick={onSplit} icon={<SquareSplitHorizontal className="size-4" />} />
          <Labelled label="Delete" danger disabled={!can.act} onClick={onDelete} icon={<Trash2 className="size-4" />} />
          <Labelled label={more ? "Fewer actions" : "More actions"} short="More" pressed={more} disabled={false} onClick={() => setMore(!more)} icon={<Ellipsis className="size-4" />} />
        </div>
        {more && (
          <div className="mt-1 grid grid-cols-4 gap-1">
            <Labelled label="Cut" disabled={!can.copy} onClick={onCut} icon={<Scissors className="size-4" />} />
            <Labelled label="Loop selection" short="Loop" disabled={!can.act} onClick={onLoop} icon={<Repeat className="size-4" />} />
            {onConsolidate && (
              <Labelled label="Consolidate" short="Join" disabled={!can.act || consolidating} onClick={onConsolidate} icon={<Combine className="size-4" />} />
            )}
            <Labelled label={muted ? "Unmute" : "Mute"} pressed={muted} disabled={!can.act} onClick={onToggleMute} icon={<VolumeX className="size-4" />} />
            <Labelled label="Back a beat" short="Beat ←" disabled={!can.act} onClick={() => onNudge(-1)} icon={<ArrowLeft className="size-4" />} />
            <Labelled label="On a beat" short="Beat →" disabled={!can.act} onClick={() => onNudge(1)} icon={<ArrowRight className="size-4" />} />
            <Labelled label="Lane above" short="Lane ↑" disabled={!can.act} onClick={() => onMoveLane(-1)} icon={<ArrowUp className="size-4" />} />
            <Labelled label="Lane below" short="Lane ↓" disabled={!can.act} onClick={() => onMoveLane(1)} icon={<ArrowDown className="size-4" />} />
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      // `basis-full` drops the row onto its own line on a phone, where the
      // containing strip wraps; inline beside the label on the wide layout,
      // where it does not. `min-w-0` is load-bearing either way: a flex child
      // defaults to min-width:auto, and without it this row would set the
      // strip's width instead of scrolling inside it.
      className="flex w-full min-w-0 basis-full items-center gap-1 overflow-x-auto scrollbar-thin lg:w-auto lg:basis-auto"
      role="toolbar"
      aria-label="Clip actions"
    >
      <Action label="Copy" keys={`${MOD}C`} disabled={!can.copy} onClick={onCopy} icon={<Copy className="size-4" />} />
      <Action label="Cut" keys={`${MOD}X`} disabled={!can.copy} onClick={onCut} icon={<Scissors className="size-4" />} />
      <Action
        label="Paste"
        keys={`${MOD}V`}
        hint="at the insert marker, over what's there"
        disabled={!can.paste}
        onClick={onPaste}
        icon={<ClipboardPaste className="size-4" />}
      />
      <Action
        label="Duplicate"
        keys={`${MOD}D`}
        disabled={!can.copy}
        onClick={onDuplicate}
        icon={<CopyPlus className="size-4" />}
      />
      <Action
        label="Split"
        keys={`${MOD}E`}
        hint="at the insert marker"
        disabled={!can.split}
        onClick={onSplit}
        icon={<SquareSplitHorizontal className="size-4" />}
      />
      <Action
        label="Loop selection"
        keys={`${MOD}L`}
        disabled={!can.act}
        onClick={onLoop}
        icon={<Repeat className="size-4" />}
      />
      {onConsolidate && (
        <Action
          label="Consolidate"
          keys={`${MOD}J`}
          hint="— each lane's part of the selection joined into one clip"
          disabled={!can.act || consolidating}
          onClick={onConsolidate}
          icon={<Combine className="size-4" />}
        />
      )}

      <Divider />

      <Action
        label="Back a beat"
        keys="←"
        disabled={!can.act}
        onClick={() => onNudge(-1)}
        icon={<ArrowLeft className="size-4" />}
      />
      <Action
        label="On a beat"
        keys="→"
        disabled={!can.act}
        onClick={() => onNudge(1)}
        icon={<ArrowRight className="size-4" />}
      />

      <Divider />

      <Action
        label="Lane above"
        keys="↑"
        disabled={!can.act}
        onClick={() => onMoveLane(-1)}
        icon={<ArrowUp className="size-4" />}
      />
      <Action
        label="Lane below"
        keys="↓"
        disabled={!can.act}
        onClick={() => onMoveLane(1)}
        icon={<ArrowDown className="size-4" />}
      />

      <Divider />

      <Action
        label={muted ? "Unmute" : "Mute"}
        keys="M"
        pressed={muted}
        disabled={!can.act}
        onClick={onToggleMute}
        icon={<VolumeX className="size-4" />}
      />
      <Action
        label="Delete"
        keys="Delete"
        danger
        disabled={!can.act}
        onClick={onDelete}
        icon={<Trash2 className="size-4" />}
      />
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-6 w-px shrink-0 bg-panelline" />;
}

function Action({
  label,
  keys,
  hint,
  icon,
  disabled,
  danger,
  pressed,
  onClick,
}: {
  label: string;
  /** The shortcut, for the tooltip and the accessible name. */
  keys: string;
  hint?: string;
  icon: ReactNode;
  disabled: boolean;
  danger?: boolean;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      // Named with its shortcut, so the tooltip teaches the keyboard and a
      // screen reader hears which key does this.
      aria-label={`${label} (${keys})`}
      aria-pressed={pressed}
      title={hint ? `${label} ${hint} — ${keys}` : `${label} — ${keys}`}
      className={cn(
        // 44px on a phone: the whole point of this row is that a thumb can hit
        // it. A pointer needs less, and gets the name from the tooltip — ten
        // labelled buttons do not fit beside the timeline at any width the
        // material shelf leaves, and a row that scrolls to reach Delete is
        // worse than a row of icons that does not.
        "flex size-11 shrink-0 items-center justify-center rounded-lg border border-panelline",
        "text-foreground/80 transition-colors hover:bg-white/5 hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-30",
        "lg:size-9",
        pressed && "bg-white/10 text-foreground",
        danger && "text-danger hover:bg-danger/10 hover:text-danger",
      )}
    >
      {icon}
    </button>
  );
}

/** A phone's action: the icon over its name, as wide as its column. */
function Labelled({
  label,
  short,
  icon,
  disabled,
  danger,
  pressed,
  onClick,
}: {
  label: string;
  /** What fits under the icon, when the full name does not. */
  short?: string;
  icon: ReactNode;
  disabled: boolean;
  danger?: boolean;
  pressed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(
        "flex h-12 min-w-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-panelline",
        "text-foreground/80 active:bg-white/10",
        "disabled:pointer-events-none disabled:opacity-30",
        pressed && "bg-white/10 text-foreground",
        danger && "text-danger active:bg-danger/10",
      )}
    >
      {icon}
      <span className="max-w-full truncate px-0.5 text-[10px] leading-none">{short ?? label}</span>
    </button>
  );
}
